import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Video,
  Search,
  Presentation,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { AppHeader } from "@/components/layout/AppHeader";
import { AppShell } from "@/components/layout/AppShell";
import { useConversationStore, type Conversation } from "@/stores/conversationStore";
import { useConversationsCount, STORE_PAGE_LIMIT } from "@/hooks/useConversations";
import { useNavigate } from "@tanstack/react-router";
import { videoLook } from "@/lib/studioB/takeover";
import { videoStage } from "@/lib/studioB/videoStage";
import { projectName } from "@/lib/projectName";
import { conversationAPI } from "@/services/api";
import { toast } from "@/hooks/use-toast";
import { VideoProjectCard } from "@/components/chat/VideoProjectCard";
import { ShareProjectDialog } from "@/components/chat/ShareProjectDialog";
import { TeamSwitcher } from "@/components/chat/TeamSwitcher";
import { useUserStore } from "@/stores/userStore";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/videos")({
  component: () => <AppShell><VideosPage /></AppShell>,
});

interface VideoProject {
  id: string;
  title: string;
  thumbnail?: string;
  slidePreviewUrls?: string[];
  duration?: string;
  status: "draft" | "processing" | "completed" | "failed";
  lastModified: Date;
  createdAt: Date;
  fileSize?: number;
  tags: string[];
  description?: string;
  projectType?: "video" | "slide";
  hasOutlineTitle?: boolean;
  stage?: string | null;
  look?: string | null;
  lookRef?: string | null;
}

const PAGE_SIZE = 24;

function conversationToProject(conv: Conversation): VideoProject {
  const isVideo = conv.collectionType === "VIDEO";
  const planJson = conv.planJson as { title?: string; lookRef?: string | null; lookLabel?: string | null } | null;
  const outlineTitle = isVideo ? planJson?.title : undefined;

  return {
    id: conv.id,
    title: projectName(conv, outlineTitle),
    hasOutlineTitle: !!outlineTitle,
    ...(isVideo ? { ...videoLook(planJson), stage: videoStage(conv) } : {}),
    thumbnail: conv.thumbnailUrl || conv.slideThumbnailUrl || conv.metadata?.projectThumbnail,
    slidePreviewUrls: conv.slidePreviewUrls || undefined,
    status: "completed" as const,
    lastModified: new Date(conv.lastActivity),
    createdAt: new Date(conv.createdAt),
    tags: [],
    projectType: isVideo ? "video" : "slide",
  };
}

function VideosPage() {
  const navigate = useNavigate();
  const { conversations, deleteConversation } = useConversationStore();
  const { activeTeam } = useUserStore();

  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "video" | "slide">("all");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const offsetRef = useRef(0);

  // Share dialog state
  const [shareDialogProject, setShareDialogProject] = useState<{ id: string; title: string } | null>(null);

  // The store holds the full list unless capped at the server page limit; when it
  // is, the VIDEO/SLIDE total comes from a shared count query (same key HeroScreen
  // uses, so the two pages dedupe to one call). Otherwise the store length is it.
  const storeCapped = conversations.length >= STORE_PAGE_LIMIT;
  const { data: serverCount } = useConversationsCount(
    activeTeam?.id ?? null,
    ['VIDEO', 'SLIDE'],
    storeCapped,
  );

  // Initial load — derive the visible list + total from the store (and the count
  // query when capped). No per-mount fetch.
  const loadInitial = useCallback(() => {
    setLoading(true);

    const relevant = conversations.filter(
      (c) => c.collectionType === "VIDEO" || c.collectionType === "SLIDE"
    );

    const mapped = relevant.map(conversationToProject);
    mapped.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());

    setProjects(mapped);
    offsetRef.current = mapped.length;

    const realTotal = storeCapped ? (serverCount ?? mapped.length) : mapped.length;
    setTotal(realTotal);
    setHasMore(mapped.length < realTotal);

    setLoading(false);
  }, [conversations, storeCapped, serverCount]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Load more pages from the API
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    try {
      const fetchFn = activeTeam?.id
        ? () => conversationAPI.getByTeam(activeTeam.id, { limit: PAGE_SIZE, offset: offsetRef.current, collectionTypes: ['VIDEO', 'SLIDE'] })
        : () => conversationAPI.getAll({ limit: PAGE_SIZE, offset: offsetRef.current, collectionTypes: ['VIDEO', 'SLIDE'] });

      const response = await fetchFn();
      const newConvs = response.conversations.filter(
        (c: any) => c.collectionType === "VIDEO" || c.collectionType === "SLIDE"
      );

      if (newConvs.length > 0) {
        const newProjects = newConvs.map((c: any) => conversationToProject(c as Conversation));
        setProjects((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const deduped = newProjects.filter((p: VideoProject) => !existingIds.has(p.id));
          return [...prev, ...deduped];
        });
      }

      const pagination = response.pagination;
      setHasMore(pagination?.hasMore ?? false);
      if (pagination?.total) setTotal(pagination.total);
      offsetRef.current += response.conversations.length;
    } catch (error) {
      console.error("Failed to load more projects:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, activeTeam]);

  // Filter projects locally
  const filtered = projects.filter((p) => {
    const matchesType = typeFilter === "all" || p.projectType === typeFilter;
    const matchesSearch =
      !searchQuery ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const handleProjectClick = (project: VideoProject) => {
    if (project.projectType === "slide") {
      navigate({
        to: "/chat/$conversationId/slide-workspace",
        params: { conversationId: project.id },
        search: { tab: undefined },
      });
    } else {
      navigate({
        to: "/chat/$conversationId/video-workspace",
        params: { conversationId: project.id },
      });
    }
  };

  const handleProjectDelete = async (project: VideoProject) => {
    try {
      await conversationAPI.delete(project.id);
      deleteConversation(project.id);
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
      setTotal((t) => t - 1);
      toast({
        title: "Project deleted",
        description: `"${project.title}" has been deleted.`,
        duration: 3000,
      });
    } catch (error) {
      console.error("Failed to delete project:", error);
      toast({
        title: "Delete failed",
        description: "Could not delete the project. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleProjectClone = async (project: VideoProject) => {
    try {
      const result = await conversationAPI.clone(project.id, {
        newTitle: `Copy of ${project.title}`,
      });
      toast({
        title: "Project cloned",
        description: `${result.slidesCopied} slides copied. Opening the new project…`,
        duration: 3000,
      });
      navigate({
        to: "/chat/$conversationId/video-workspace",
        params: { conversationId: result.newProjectId },
      });
    } catch (error) {
      console.error("Failed to clone project:", error);
      toast({
        title: "Clone failed",
        description: "Could not clone the project. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen studio-canvas">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto studio-canvas">
      <AppHeader />

      {/* Page title + search */}
      <div className="w-full px-6 md:px-10 pt-8 pb-4">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h1 className="text-3xl font-bold tracking-tight studio-text-primary">
              Videos
            </h1>
            <p className="text-sm studio-text-secondary mt-1.5">
              {total} project{total !== 1 ? "s" : ""}
            </p>
          </div>
          <TeamSwitcher className="w-56" />
        </div>

        {/* Search + Filter row */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/70 dark:bg-black/30 backdrop-blur-md border-black/10 dark:border-white/10"
            />
          </div>

          {/* Type filter tabs */}
          <div className="flex items-center gap-0.5 p-1 bg-black/5 dark:bg-white/5 rounded-xl">
            {(["all", "video", "slide"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={cn(
                  "px-4 py-1.5 text-sm font-medium rounded-lg transition-all duration-200",
                  typeFilter === t
                    ? "bg-white dark:bg-white/10 studio-text-primary shadow-sm"
                    : "studio-text-secondary hover:studio-text-primary"
                )}
              >
                {t === "all" ? "All" : t === "video" ? "Videos" : "Slides"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full px-6 md:px-10 py-8">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-20 h-20 bg-black/5 dark:bg-white/5 rounded-full flex items-center justify-center mb-6">
              {typeFilter === "slide" ? (
                <Presentation className="w-10 h-10 text-muted-foreground" />
              ) : (
                <Video className="w-10 h-10 text-muted-foreground" />
              )}
            </div>
            <h3 className="text-lg font-semibold studio-text-primary mb-2">
              {searchQuery ? "No matching projects" : "No projects yet"}
            </h3>
            <p className="text-sm studio-text-secondary mb-6 max-w-sm">
              {searchQuery
                ? "Try a different search term."
                : "Create your first project from the home page."}
            </p>
            {!searchQuery && (
              <Button onClick={() => navigate({ to: "/", search: { slide: undefined } })}>
                Go to Home
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="text-sm studio-text-secondary mb-4">
              Showing {filtered.length} of {total} project{total !== 1 ? "s" : ""}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((project) => (
                <VideoProjectCard
                  key={project.id}
                  project={project}
                  onClick={handleProjectClick}
                  onDelete={handleProjectDelete}
                  onShare={(p) => setShareDialogProject({ id: p.id, title: p.title })}
                  onClone={handleProjectClone}
                  onAnotherInLook={(lookRef) => navigate({ to: "/", search: { slide: undefined, look: lookRef } })}
                  className="transform transition-all duration-300 hover:scale-[1.02]"
                />
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div className="mt-8 text-center">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-8"
                >
                  {loadingMore ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ChevronDown className="w-4 h-4 mr-2" />
                  )}
                  {loadingMore ? "Loading..." : "Load more projects"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Share Project Dialog */}
      {shareDialogProject && (
        <ShareProjectDialog
          open={!!shareDialogProject}
          onOpenChange={(open) => { if (!open) setShareDialogProject(null); }}
          conversationId={shareDialogProject.id}
          projectTitle={shareDialogProject.title}
        />
      )}
    </div>
  );
}
